/**
 * Carrier detection for a Mail-on-Demand tracking reference.
 *
 * The print shop stores a raw tracking number on the order (`shipping.trackingRef`);
 * we never persist the carrier. This derives the carrier + a deep link to its
 * public tracking page purely from the number's shape, so the member can follow
 * a shipped mailing in one click. Read-only — no DS/order change.
 *
 * Detection (first match wins, ref trimmed first):
 *  - starts with `1Z` (case-insensitive)            → UPS
 *  - exactly 12 or 15 digits                        → FedEx
 *  - 20–22 digits starting with `96`                → FedEx (Ground/SmartPost)
 *  - 20–22 digits starting with `92`/`93`/`94`/`95` → USPS (IMpb)
 *  - 20–22 digits, any other prefix                 → unknown (ambiguous, no link)
 *  - `^[A-Z]{2}\d{9}[A-Z]{2}$` (S10, case-insensitive) → USPS (international)
 *  - anything else                                  → unknown (no link)
 */
export const CARRIER = {
  UPS: 'ups',
  FEDEX: 'fedex',
  USPS: 'usps',
};

const CARRIER_URL_BUILDERS = {
  [CARRIER.UPS]: (ref) => `https://www.ups.com/track?tracknum=${encodeURIComponent(ref)}`,
  [CARRIER.FEDEX]: (ref) =>
    `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(ref)}`,
  [CARRIER.USPS]: (ref) =>
    `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(ref)}`,
};

const isAllDigits = (value) => /^\d+$/.test(value);

// USPS S10 international format: 2 letters, 9 digits, 2 letters (e.g. CP123456789US).
const S10_INTERNATIONAL = /^[A-Z]{2}\d{9}[A-Z]{2}$/i;

const detectCarrier = (ref) => {
  if (/^1Z/i.test(ref)) {
    return CARRIER.UPS;
  }

  if (isAllDigits(ref)) {
    const { length } = ref;

    if (length === 12 || length === 15) {
      return CARRIER.FEDEX;
    }

    if (length >= 20 && length <= 22) {
      // The 20–22-digit window overlaps between FedEx and USPS, so disambiguate
      // on the leading prefix instead of routing everything to USPS.
      if (ref.startsWith('96')) {
        return CARRIER.FEDEX; // FedEx Ground / SmartPost barcodes.
      }

      if (/^9[2-5]/.test(ref)) {
        return CARRIER.USPS; // USPS IMpb (92/93/94/95).
      }

      return null; // Ambiguous prefix → no link (safe fallback).
    }
  }

  if (S10_INTERNATIONAL.test(ref)) {
    return CARRIER.USPS;
  }

  return null;
};

/**
 * @param {string} trackingRef raw tracking reference from the order
 * @returns {{ carrier: string|null, url: string|null }}
 *   `carrier`/`url` are null when the format is unrecognized (show the number,
 *   no link).
 */
export const resolveCarrier = (trackingRef) => {
  const ref = String(trackingRef ?? '').trim();

  if (!ref) {
    return { carrier: null, url: null };
  }

  const carrier = detectCarrier(ref);

  if (!carrier) {
    return { carrier: null, url: null };
  }

  return { carrier, url: CARRIER_URL_BUILDERS[carrier](ref) };
};
